import { authenticatedFetch } from "../lib/auth";
import { ForbiddenError } from "../lib/forbiddenError";

export type E2EAgentTransportOverride = (
  path: string,
  init: RequestInit,
) => Promise<Response>;

let transportOverride: E2EAgentTransportOverride | null = null;

/** Install the MCP App's host-proxied transport before the frontend mounts. */
export function installE2EAgentTransportOverride(
  transport: E2EAgentTransportOverride,
): void {
  if (transportOverride !== null) {
    throw new Error("The E2E-agent transport override is already installed.");
  }
  transportOverride = transport;
}

export class E2EAgentConflictError extends Error {
  readonly currentRevision: number | null;

  constructor(message: string, currentRevision: number | null) {
    super(message);
    this.name = "E2EAgentConflictError";
    this.currentRevision = currentRevision;
  }
}

const PUBLIC_PATHS = [
  /^\/v1\/configuration$/,
  /^\/v1\/companies(?:\/[^/]+)?$/,
  /^\/v1\/companies\/[^/]+\/documents$/,
  /^\/v1\/companies\/[^/]+\/documents\/list$/,
  /^\/v1\/companies\/[^/]+\/documents\/[^/]+\/link$/,
  /^\/v1\/companies\/[^/]+\/models$/,
  /^\/v1\/companies\/[^/]+\/models\/latest$/,
  /^\/v1\/companies\/[^/]+\/models\/[^/]+\/chat-threads$/,
  /^\/v1\/companies\/[^/]+\/models\/[^/]+(?:\/(?:xlm|annotations|annotation-revisions|summary))?$/,
  /^\/v1\/companies\/[^/]+\/models\/[^/]+\/xlm\/(?:workbook|excel-projection|delta|revisions(?:\/\d+(?:\/restore)?)?)$/,
  /^\/v1\/chat-edit-proposals\/[^/]+\/(?:approve|reject)$/,
  /^\/v1\/chat-threads\/[^/]+(?:\/turns)?$/,
  /^\/v1\/inputs$/,
  /^\/v1\/inputs\/[^/]+\/(?:complete|content)$/,
  /^\/v1\/runs\/catalog$/,
  /^\/v1\/runs(?:\/[^/]+(?:\/(?:start|cancel|artifact-links|visibility))?)?$/,
  /^\/v1\/runs\/[^/]+\/inputs\/[^/]+$/,
  /^\/v1\/models$/,
] as const;

const ANALYST_POST_PATHS = [
  /^\/v1\/companies\/[^/]+\/documents\/list$/,
  /^\/v1\/companies\/[^/]+\/documents\/[^/]+\/link$/,
  /^\/v1\/companies\/[^/]+\/models\/[^/]+\/annotation-revisions$/,
  /^\/v1\/companies\/[^/]+\/models\/[^/]+\/xlm\/revisions$/,
  /^\/v1\/companies\/[^/]+\/models\/[^/]+\/xlm\/revisions\/\d+\/restore$/,
  /^\/v1\/companies\/[^/]+\/models\/[^/]+\/chat-threads$/,
  /^\/v1\/chat-edit-proposals\/[^/]+\/(?:approve|reject)$/,
  /^\/v1\/chat-threads\/[^/]+\/turns$/,
  /^\/v1\/runs$/,
  /^\/v1\/runs\/[^/]+\/start$/,
  /^\/v1\/runs\/[^/]+\/artifact-links$/,
  /^\/v1\/runs\/[^/]+\/inputs\/[^/]+$/,
] as const;

function decodesToDotSegment(segment: string): boolean {
  let decoded = segment;
  let stable = false;
  for (let pass = 0; pass <= segment.length; pass += 1) {
    try {
      const next = decodeURIComponent(decoded);
      if (next === decoded) {
        stable = true;
        break;
      }
      decoded = next;
    } catch {
      return true;
    }
  }
  if (!stable) return true;
  return decoded
    .replace(/\\/g, "/")
    .split("/")
    .some((part) => part === "." || part === "..");
}

function isPublicPath(path: string): boolean {
  const rawPath = path.split(/[?#]/, 1)[0] ?? "";
  if (
    !rawPath.startsWith("/v1/") ||
    rawPath.includes("\\") ||
    rawPath.split("/").some(decodesToDotSegment)
  ) {
    return false;
  }
  try {
    const url = new URL(path, "https://agent.invalid");
    return (
      url.origin === "https://agent.invalid" &&
      PUBLIC_PATHS.some((pattern) => pattern.test(url.pathname))
    );
  } catch {
    return false;
  }
}

function withAnalystView(path: string): string {
  const url = new URL(path, "https://agent.invalid");
  if (
    url.origin !== "https://agent.invalid" ||
    url.hash !== "" ||
    url.searchParams.has("view")
  ) {
    throw new Error("E2E-agent analyst projection must be transport-owned.");
  }
  url.searchParams.set("view", "analyst");
  return `${url.pathname}${url.search}`;
}

function hasAnalystView(path: string): boolean {
  const url = new URL(path, "https://agent.invalid");
  return (
    url.searchParams.getAll("view").length === 1 &&
    url.searchParams.get("view") === "analyst"
  );
}

function detailMessage(body: unknown): string | null {
  if (!body || typeof body !== "object" || !("detail" in body)) return null;
  const detail = body.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (item && typeof item === "object" && "msg" in item) {
          return String(item.msg);
        }
        return JSON.stringify(item);
      })
      .join("; ");
  }
  return JSON.stringify(detail);
}

async function responseError(response: Response): Promise<Error> {
  let suffix = "";
  let body: unknown;
  try {
    body = await response.json();
    const detail = detailMessage(body);
    if (detail) suffix = `: ${detail}`;
  } catch {
    // Status-only errors remain useful when the backend returned no JSON.
  }
  const message = `${response.status} ${response.statusText}${suffix}`;
  if (response.status === 403) return new ForbiddenError(`403${suffix}`);
  if (response.status === 409) {
    let currentRevision: number | null = null;
    if (body !== null && typeof body === "object" && !Array.isArray(body)) {
      const value = (body as Record<string, unknown>).current_revision;
      if (typeof value === "number" && Number.isInteger(value) && value >= 0) {
        currentRevision = value;
      }
    }
    return new E2EAgentConflictError(message, currentRevision);
  }
  return new Error(message);
}

async function send(
  path: string,
  init: RequestInit,
  analystProjection = false,
): Promise<Response> {
  if (!isPublicPath(path)) {
    throw new Error("Path is outside the E2E-agent API allowlist.");
  }
  if (analystProjection && !hasAnalystView(path)) {
    throw new Error("E2E-agent request lacks view=analyst.");
  }
  if (transportOverride !== null) {
    return transportOverride(path, init);
  }
  return authenticatedFetch(`/api${path}`, init);
}

export async function e2eAgentJson(
  path: string,
  signal?: AbortSignal,
): Promise<unknown> {
  const response = await e2eAgentResponse(path, signal);
  return response.json() as Promise<unknown>;
}

export async function e2eAgentResponse(
  path: string,
  signal?: AbortSignal,
): Promise<Response> {
  const response = await send(withAnalystView(path), { signal }, true);
  if (!response.ok) throw await responseError(response);
  return response;
}

export async function postE2EAgentJson(
  path: string,
  body?: unknown,
  headers?: Record<string, string>,
): Promise<unknown> {
  const requestHeaders = new Headers(headers);
  if (body !== undefined)
    requestHeaders.set("Content-Type", "application/json");
  const rawPath = path.split(/[?#]/, 1)[0] ?? "";
  const analystProjection = ANALYST_POST_PATHS.some((pattern) =>
    pattern.test(rawPath),
  );
  const requestPath = analystProjection ? withAnalystView(path) : path;
  const response = await send(
    requestPath,
    {
      method: "POST",
      headers: requestHeaders,
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    analystProjection,
  );
  if (!response.ok) throw await responseError(response);
  return response.json() as Promise<unknown>;
}

export async function patchE2EAgentJson(
  path: string,
  body: unknown,
): Promise<unknown> {
  const requestPath = withAnalystView(path);
  const response = await send(
    requestPath,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    true,
  );
  if (!response.ok) throw await responseError(response);
  return response.json() as Promise<unknown>;
}

export async function deleteE2EAgent(path: string): Promise<void> {
  const response = await send(
    withAnalystView(path),
    { method: "DELETE" },
    true,
  );
  if (!response.ok) throw await responseError(response);
}

export async function deleteE2EAgentJson(path: string): Promise<unknown> {
  const response = await send(
    withAnalystView(path),
    { method: "DELETE" },
    true,
  );
  if (!response.ok) throw await responseError(response);
  return response.json() as Promise<unknown>;
}

export async function putE2EAgentUpload(
  url: string,
  headers: Record<string, string>,
  file: File,
): Promise<void> {
  const resolved = new URL(url, window.location.origin);
  if (
    resolved.protocol !== "https:" &&
    resolved.origin !== window.location.origin
  ) {
    throw new Error("Input upload URL is not an authorized HTTPS destination.");
  }
  if (resolved.username || resolved.password) {
    throw new Error("Input upload URL must not contain credentials.");
  }
  const requestHeaders = new Headers(headers);
  if (resolved.origin === window.location.origin) {
    if (!resolved.pathname.startsWith("/api/")) {
      throw new Error("Same-origin uploads must use the E2E-agent API proxy.");
    }
    const apiPath = `${resolved.pathname.slice("/api".length)}${resolved.search}`;
    if (!isPublicPath(apiPath)) {
      throw new Error("Upload path is outside the E2E-agent API allowlist.");
    }
  }
  const init: RequestInit = {
    method: "PUT",
    headers: requestHeaders,
    body: file,
  };
  let response: Response;
  if (resolved.origin === window.location.origin) {
    response = await authenticatedFetch(resolved, init);
  } else {
    response = await fetch(resolved, init);
  }
  if (!response.ok) throw await responseError(response);
}
